import { Box, Chip, IconButton } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";
import ConfirmationModal from "components/modals/confirmation-modal";
import { Status } from "contexts/api-key-state";

import { GridColDef } from "@mui/x-data-grid-premium";
import * as QueryKeys from "data";
import { ApiKey } from "data/models";
import { deleteApiKey } from "data/mutations";
import { Gem, Pencil, Trash } from "lucide-react";
import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { generatePath, useHistory } from "react-router-dom";
import * as Routes from "routes";
import { formatTimestamp } from "utils";
import { premiumProducts } from "./api-keys-page";

const renderProductChips = (products: string[]) => {
  let extraProducts: string[] = [];

  return (
    <>
      {products.map((product: string, index: number) => {
        if (index < 2)
          return (
            <Chip key={product} label={product} style={{ marginLeft: "8px" }} />
          );
        extraProducts.push(product);
        return <></>;
      })}
      {extraProducts.length > 0 && (
        <Chip
          title={extraProducts.join(", ")}
          label={"+" + extraProducts.length}
          style={{ marginLeft: "8px" }}
        />
      )}
    </>
  );
};

export const useColumns = (identityId: string) => {
  const history = useHistory();

  const queryClient = useQueryClient();

  const apiKeyDeleteMutation = useMutation(
    (selectedKey: string) => deleteApiKey(selectedKey),
    {
      onSuccess: (deletedApiKey) => {
        queryClient.setQueryData<ApiKey[]>(
          [QueryKeys.apiKeys, identityId],
          (oldData) => {
            if (!oldData) return [];
            return oldData.filter(
              (key: ApiKey) => key.name !== deletedApiKey.name
            );
          }
        );

        setDeleteModalIsOpen(false);
      },
    }
  );

  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);

  return [
    { flex: 1, field: "name", headerName: "Name" },
    { flex: 1, field: "creator", headerName: "Creator" },
    {
      flex: 1,
      field: "premium",
      headerName: "Premium",
      renderCell: (params: any) => {
        if (
          params.row.products.some((product: any) =>
            premiumProducts.includes(product)
          )
        ) {
          return (
            <Chip
              size="small"
              icon={<Gem color="#1876d3" width={"20px"} />}
              label={"Premium"}
              variant="outlined"
              color="primary"
            />
          );
        }
      },
    },

    {
      flex: 2,
      field: "products",
      headerName: "Products",

      renderCell: (params: any) => {
        return renderProductChips(params.row.products);
      },
    },
    {
      flex: 1,
      field: "createdDate",
      headerName: "Create Date",

      renderCell: (params: GridRenderCellParams) =>
        formatTimestamp(params.row.createdDate),
    },
    {
      flex: 1,
      field: "expireDate",
      headerName: "Expiry Date",

      renderCell: (params: GridRenderCellParams) =>
        formatTimestamp(params.row.expireDate),
    },
    {
      field: "status",
      headerName: "Status",

      disableColumnMenu: true,
      sortable: false,
      editable: false,
      width: 200,

      renderCell: (params: GridRenderCellParams) => (
        <StatusGroup
          status={params.row.status}
          expireDate={params.row.expireDate}
        />
      ),
    },
    {
      flex: 1.2,
      field: "id",
      headerName: "Actions",
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box sx={{ display: "block", gap: 0 }}>
            <IconButton
              color="info"
              onClick={async () => {
                try {
                  history.push(
                    generatePath(Routes.developerApiKeysEditKey, {
                      keyName: params.id,
                    }),
                    {
                      keyName: params.id,
                    }
                  );
                } catch (er) {}
              }}
            >
              <Pencil />
            </IconButton>

            <IconButton
              color="error"
              onClick={() => {
                setSelectedKeyId(params.row.id as string);

                setDeleteModalIsOpen(true);
              }}
            >
              <Trash />
            </IconButton>
            <ConfirmationModal
              isLoading={apiKeyDeleteMutation.isLoading}
              isError={apiKeyDeleteMutation.isError}
              open={deleteModalIsOpen}
              header={`Are you sure?`}
              body={`Deleting "${selectedKeyId}" API Key will permanently remove it from your account. This action cannot be undone.`}
              leftButtonText={"Yes, Delete API Key"}
              leftButtonColor="error"
              rightButtonText={"No, Keep API Key"}
              rightButtonColor="primary"
              handleLeftButton={() => {
                if (selectedKeyId) {
                  apiKeyDeleteMutation.mutate(selectedKeyId);
                }
              }}
              handleRightButton={() => {
                setDeleteModalIsOpen(false);
                setSelectedKeyId(null);
              }}
              handleClose={() => {
                setDeleteModalIsOpen(false);
                setSelectedKeyId(null);
              }}
            />
          </Box>
        );
      },
    },
  ];
};
const StatusGroup = (props: { status: Status; expireDate: number }) => {
  /**
   * Returns rendered chip(s) of the status of an API key
   */

  const color =
    props.status === "approved"
      ? "success"
      : props.status === "revoked"
      ? "error"
      : "info";

  let expired = false;

  if (props.expireDate !== -1 && props.expireDate <= Math.round(Date.now())) {
    expired = true;
  }

  return (
    <Box
      style={{
        display: "flex",
        gap: "5%",
        height: "100%",
        alignItems: "center",
      }}
    >
      {expired ? (
        <Chip
          label="Expired"
          color="primary"
          style={{ backgroundColor: "red" }}
        />
      ) : (
        <Chip
          label={props.status}
          color={color}
          style={{ textTransform: "uppercase" }}
        />
      )}
    </Box>
  );
};

export const columnsAdmin: GridColDef[] = [
  { flex: 1, field: "name", headerName: "Name" },
  {
    flex: 1,
    field: "createdDate",
    headerName: "Create Date",

    renderCell: (params: GridRenderCellParams) =>
      formatTimestamp(params.row.createdDate),
  },
  {
    flex: 1,
    field: "expireDate",
    headerName: "Expiry Date",

    renderCell: (params: GridRenderCellParams) =>
      formatTimestamp(params.row.expireDate),
  },
  {
    field: "status",
    headerName: "Status",

    disableColumnMenu: true,
    sortable: false,
    editable: false,
    width: 200,

    renderCell: (params: GridRenderCellParams) => (
      <StatusGroup
        status={params.row.status}
        expireDate={params.row.expireDate}
      />
    ),
  },
];
